((factory) => {
  const api = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (typeof window !== 'undefined') {
    window.HanezzaReviewTestimonialSync = api;
  }

  if (typeof document !== 'undefined') {
    api.boot(document);
  }
})(() => {
  'use strict';

  const TESTIMONIAL_ROOT = '[data-hanezza-testimonials]';
  const REVIEW_ROOT = '[data-hanezza-brand-reviews]';
  const REVIEW_WIDGET = '[data-brand-review-widget]';
  const REVIEW_CARD = '.hanezza-air-review-card';
  const RESPONSE_LABELS = ['mağaza yanıtladı', 'satıcı yanıtladı', 'store replied', 'merchant reply'];
  const VERIFIED_LABELS = ['doğrulanmış değerlendirme', 'doğrulanmış alışveriş', 'verified review', 'verified purchase'];

  const normalize = (value = '') => String(value).toLocaleLowerCase('tr-TR').replace(/\s+/g, ' ').trim();
  const clean = (value = '') => String(value).replace(/\s+/g, ' ').trim();

  function includesAny(value, needles) {
    const normalized = normalize(value);
    return needles.some((needle) => normalized.includes(needle));
  }

  function parseRating(labels = []) {
    for (const source of labels) {
      const value = clean(source);
      if (!value) continue;

      const patterns = [
        /5\s*üzerinden\s*([0-5](?:[.,]\d+)?)/i,
        /([0-5](?:[.,]\d+)?)\s*out\s+of\s+5/i,
        /([0-5](?:[.,]\d+)?)\s*(?:yıldız|stars?)/i
      ];

      for (const pattern of patterns) {
        const match = value.match(pattern);
        if (!match) continue;
        const parsed = Number.parseFloat(match[1].replace(',', '.'));
        if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 5) return Math.round(parsed);
      }
    }

    return null;
  }

  function projectReviewData({ paragraphs = [], strongs = [], ratingLabels = [], allText = '' } = {}) {
    const quote = paragraphs.map(clean).find(Boolean) || '';
    const author = strongs
      .map(clean)
      .filter(Boolean)
      .filter((value) => !includesAny(value, RESPONSE_LABELS))
      .filter((value) => !/^\d(?:[.,]\d+)?\s*(?:\/\s*5)?$/.test(value))
      .at(-1) || '';
    const rating = parseRating(ratingLabels);

    if (!quote || !author || rating === null) return null;

    const verified = includesAny(allText, VERIFIED_LABELS);
    return {
      quote,
      author,
      rating,
      verified,
      meta: verified ? 'Doğrulanmış alışveriş' : ''
    };
  }

  function extractReviewCard(card) {
    if (!card) return null;

    const paragraphs = Array.from(card.querySelectorAll('p')).map((element) => element.textContent || '');
    const strongs = Array.from(card.querySelectorAll('strong, b, h4, h5, [class*="name" i], [class*="author" i]'))
      .map((element) => element.textContent || '');
    const ratingLabels = Array.from(card.querySelectorAll('[aria-label*="yıldız" i], [aria-label*="star" i], [data-rating]'))
      .flatMap((element) => [element.getAttribute('aria-label'), element.getAttribute('data-rating'), element.textContent])
      .filter(Boolean);

    return projectReviewData({
      paragraphs,
      strongs,
      ratingLabels,
      allText: card.textContent || ''
    });
  }

  function collectFeaturedReviews(reviewRoot, limit = 3) {
    if (!reviewRoot) return [];
    return Array.from(reviewRoot.querySelectorAll(REVIEW_CARD))
      .map(extractReviewCard)
      .filter(Boolean)
      .slice(0, limit);
  }

  function renderCard(card, review) {
    card.hidden = !review;
    if (!review) return;

    const stars = card.querySelector('[data-testimonial-stars]');
    const quote = card.querySelector('[data-testimonial-quote]');
    const avatar = card.querySelector('[data-testimonial-avatar]');
    const author = card.querySelector('[data-testimonial-author]');
    const meta = card.querySelector('[data-testimonial-meta]');
    const verified = card.querySelector('[data-testimonial-verified]');

    if (stars) {
      stars.setAttribute('aria-label', `5 üzerinden ${review.rating} yıldız`);
      Array.from(stars.querySelectorAll('[data-testimonial-star]')).forEach((star, index) => {
        star.classList.toggle('is-empty', index + 1 > review.rating);
      });
    }
    if (quote) quote.textContent = review.quote;
    if (avatar) avatar.textContent = review.author.slice(0, 1).toLocaleUpperCase('tr-TR');
    if (author) author.textContent = review.author;
    if (meta) {
      meta.textContent = review.meta;
      meta.hidden = !review.meta;
    }
    if (verified) verified.hidden = !review.verified;
  }

  function sync(scope = document) {
    const testimonialRoot = scope.querySelector?.(TESTIMONIAL_ROOT) || document.querySelector(TESTIMONIAL_ROOT);
    const reviewRoot = document.querySelector(REVIEW_ROOT);
    if (!testimonialRoot || !reviewRoot) return 0;

    const reviews = collectFeaturedReviews(reviewRoot, 3);
    const cards = Array.from(testimonialRoot.querySelectorAll('[data-hanezza-testimonial-card]'));
    cards.forEach((card, index) => renderCard(card, reviews[index] || null));

    testimonialRoot.hidden = reviews.length === 0;
    testimonialRoot.dataset.reviewSource = reviews.length ? 'ag-product-reviews' : 'pending';
    return reviews.length;
  }

  function observeReviewWidget() {
    const reviewRoot = document.querySelector(REVIEW_ROOT);
    const widget = reviewRoot?.querySelector(REVIEW_WIDGET);
    if (!widget || widget.dataset.hanezzaTestimonialObserved === 'true') return;

    widget.dataset.hanezzaTestimonialObserved = 'true';
    let frame = 0;
    const observer = new MutationObserver(() => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => sync(document));
    });
    observer.observe(widget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-label']
    });
    widget.hanezzaTestimonialObserver = observer;
  }

  function boot(scope = document) {
    const run = () => {
      observeReviewWidget();
      sync(scope);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }

    document.addEventListener('shopify:section:load', run);
  }

  return {
    parseRating,
    projectReviewData,
    extractReviewCard,
    collectFeaturedReviews,
    sync,
    boot
  };
});
